import { Calendar, Users } from 'lucide-react';
import type { Project } from '../../types/projects';
import { AGENTS, DEPARTMENTS, getAgentColor } from '../../utils/agents';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

const STATUS_MAP: Record<string, { label: string; dot: string; bg: string; text: string }> = {
  active: { label: 'Active', dot: 'bg-success', bg: 'bg-success/15', text: 'text-success' },
  completed: { label: 'Completed', dot: 'bg-info', bg: 'bg-info/15', text: 'text-info' },
  archived: { label: 'Archived', dot: 'bg-text-muted', bg: 'bg-text-muted/15', text: 'text-text-muted' },
};

function getDaysRemaining(targetDate: string | null): { label: string; isOverdue: boolean } | null {
  if (!targetDate) return null;
  const target = new Date(targetDate + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, isOverdue: true };
  if (diffDays === 0) return { label: 'Due today', isOverdue: false };
  if (diffDays === 1) return { label: '1d remaining', isOverdue: false };
  return { label: `${diffDays}d remaining`, isOverdue: false };
}

export default function ProjectCard({ project, onClick }: ProjectCardProps) {
  const agent = project.lead_agent ? AGENTS.find((a) => a.slug === project.lead_agent) : null;
  const dept = project.department ? DEPARTMENTS.find((d) => d.id === project.department) : null;
  const status = STATUS_MAP[project.status] || STATUS_MAP.active;
  const counts = project.taskCounts || { backlog: 0, in_progress: 0, completed: 0, total: 0 };
  const progressPct = counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0;
  const daysRemaining = getDaysRemaining(project.target_date);

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="bg-bg-secondary border border-border-subtle rounded-lg p-4 hover:border-border-hover cursor-pointer transition-colors flex flex-col gap-3"
    >
      {/* Header: name + status */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium text-text-primary leading-tight line-clamp-2">
          {project.name}
        </h3>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full shrink-0 ${status.bg}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
          <span className={`text-xs font-medium ${status.text}`}>{status.label}</span>
        </span>
      </div>

      {/* Description */}
      {project.description ? (
        <p className="text-xs text-text-muted line-clamp-2">{project.description}</p>
      ) : null}

      {/* Progress bar */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-muted">Progress</span>
          <span className="text-text-secondary">{progressPct}%</span>
        </div>
        <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Task count chips */}
      {counts.total > 0 ? (
        <div className="flex gap-2 flex-wrap">
          {counts.backlog > 0 ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-text-muted/15 text-text-muted">
              {counts.backlog} backlog
            </span>
          ) : null}
          {counts.in_progress > 0 ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-warning/15 text-warning">
              {counts.in_progress} in progress
            </span>
          ) : null}
          {counts.completed > 0 ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-success/15 text-success">
              {counts.completed} completed
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Footer: lead agent, department, target date */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle">
        <div className="flex items-center gap-2 min-w-0">
          {agent ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <div className={`h-5 w-5 rounded-full ${getAgentColor(agent.department)} flex items-center justify-center shrink-0`}>
                <Users className="h-3 w-3 text-white" />
              </div>
              <span className="text-xs text-text-secondary truncate">{agent.shortName}</span>
            </div>
          ) : null}
          {dept ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-bg-tertiary text-text-muted truncate">
              {dept.name}
            </span>
          ) : null}
        </div>

        {daysRemaining ? (
          <div className={`flex items-center gap-1 shrink-0 ${daysRemaining.isOverdue ? 'text-error' : 'text-text-muted'}`}>
            <Calendar className="h-3 w-3" />
            <span className="text-xs">{daysRemaining.label}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
